import { useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { bills, type BillUploadResult } from '../api/client';
import { useToast } from './Toast';
import { OcrBadge } from './BillViewer';
import { formatBytes } from '../lib/format';

/**
 * Drag-and-drop (or click) upload of one bill. Uploads immediately and then
 * hands the created bill to `onUploaded`, which owns the OCR polling.
 */
export function BillDropzone({
  transactionId,
  onUploaded,
  compact,
}: {
  transactionId?: string;
  onUploaded?: (bill: BillUploadResult) => void;
  compact?: boolean;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<BillUploadResult | null>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const upload = async (file: File | undefined) => {
    if (!file) return;
    setBusy(true);
    try {
      const created = await bills.upload(file, transactionId);
      setResult(created);
      toast(
        created.deduplicated
          ? 'Identical bill was already stored — reusing it'
          : `Uploaded ${file.name} (${formatBytes(file.size)})`,
        'success',
      );
      queryClient.invalidateQueries({ queryKey: ['payments'] });
      queryClient.invalidateQueries({ queryKey: ['bills'] });
      onUploaded?.(created);
    } catch (error) {
      toast(error instanceof Error ? error.message : 'Upload failed', 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <div
        className={`dropzone${over ? ' dropzone--over' : ''}`}
        role="button"
        tabIndex={0}
        aria-label="Upload a bill (PDF or screenshot)"
        onClick={() => inputRef.current?.click()}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            inputRef.current?.click();
          }
        }}
        onDragOver={(event) => {
          event.preventDefault();
          setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={(event) => {
          event.preventDefault();
          setOver(false);
          void upload(event.dataTransfer.files[0]);
        }}
      >
        {busy ? (
          <span className="spinner spinner--lg" aria-hidden="true" />
        ) : (
          <>
            <div className="dropzone__big">{compact ? 'Drop a bill' : 'Drop a bill here or click to choose'}</div>
            <div>PDF, PNG, JPEG, WEBP — up to 20 MB</div>
          </>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="application/pdf,image/png,image/jpeg,image/webp"
        hidden
        onChange={(event) => {
          void upload(event.target.files?.[0]);
          event.target.value = '';
        }}
      />
      {result ? (
        <p className="ocr-state" style={{ marginTop: 'var(--space-2)' }}>
          OCR: <OcrBadge status={result.ocrStatus} error={result.ocrError} />
        </p>
      ) : null}
    </div>
  );
}
